import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Alert,
  Box,
  Fab,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ResponseOfMenuItem } from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";

const useStyles = (theme: Theme) => ({
  selectedIcon: {
    color: theme.palette.secondary.main,
  },
});

const MenuItemDetails = () => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    menuItemsUrl,
    menuItemEditUrl,
    menusUrl,
    menuService,
  } = useGlobalContext();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();

  const menuItemResult = useQuery<ResponseOfMenuItem>(
    ["menuItem", id],
    () => menuService.getMenuItemById(id!),
    {
      enabled: !!id,
    }
  );

  const menuItem = menuItemResult.data?.data;
  useTitle(menuItem?.name || "Menu Item Details");

  if (!id) return <Navigate to={menuItemsUrl()} replace />;
  if (menuItemResult.isLoading) return <Loader />;
  if (menuItemResult.isError) {
    console.log(menuItemResult.error);

    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="menuLoadError" />
        </Alert>
      </Box>
    );
  }

  const handleDelete = async () => {
    try {
      await registerPromise(menuService.deleteMenuItem(id));
      queryClient.invalidateQueries("menuItems");
      deleteOpenActions.setFalse();
      navigate(menuItemsUrl());
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const rows = [
    {
      title: t({ id: "id" }),
      component: menuItem?.id && <span>{menuItem.id}</span>,
    },
    {
      title: t({ id: "nameLabel" }),
      component: menuItem?.name && <span>{menuItem.name}</span>,
    },
    {
      title: t({ id: "descriptionLabel" }),
      component: menuItem?.description && <span>{menuItem.description}</span>,
    },
    {
      title: t({ id: "toolTipLabel" }),
      component: menuItem?.toolTip && <span>{menuItem.toolTip}</span>,
    },
    {
      title: t({ id: "urlLabel" }),
      component: menuItem?.url && <span>{menuItem.url}</span>,
    },
    {
      title: t({ id: "claimLabel" }),
      component: menuItem?.claim && <span>{menuItem.claim}</span>,
    },
    {
      title: t({ id: "typeLabel" }),
      component: menuItem?.menuItemTypeName && (
        <span>{menuItem.menuItemTypeName}</span>
      ),
    },
    {
      title: t({ id: "iconLabel" }),
      component: menuItem?.icon && (
        <IconButton color="primary" size="medium" component="span" disabled>
          <Icon
            fontSize="large"
            className={menuItem?.icon}
            sx={styles.selectedIcon}
          />
        </IconButton>
      ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <RedirectBackButton
        redirectRoute={menuItemsUrl()}
        labelKey="backToListButtonLabel"
        style={{ marginRight: "10px" }}
      />

      <RedirectBackButton
        redirectRoute={menusUrl()}
        labelKey="goToMenusLabel"
        icon={<MenuIcon />}
      />
      <h1>
        <span>{menuItem?.name}</span>
      </h1>

      <Box my={3} display="flex" justifyContent="center">
        <Link to={menuItemEditUrl(id)} style={{ textDecoration: "none" }}>
          <Fab color="primary" data-cy="editButton">
            <EditIcon />
          </Fab>
        </Link>
        <Box ml={4} />
        <Fab
          color="secondary"
          onClick={deleteOpenActions.setTrue}
          data-cy="deleteButton"
        >
          <DeleteIcon />
        </Fab>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold" }}
                >
                  {title}
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteEntityDialog
        dialogTitleKey="menuItemDeleteTitle"
        contentKey="menuItemDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
    </>
  );
};

export default MenuItemDetails;
